/**
 * AllergenIcons — EU 14 mandatory allergen SVG icons.
 * Each icon is a simple, recognizable symbol at 24×24 default.
 */

const iconPaths = {
  gluten: (
    <path d="M12 2C11 5 9 7 9 10c0 2 1 3 3 3s3-1 3-3c0-3-2-5-3-8zm-3 12c-1 0-2 1-2 2v6h2v-4h2v4h2v-4h2v4h2v-6c0-1-1-2-2-2H9z" />
  ),
  crustaceans: (
    <path d="M12 4c-2 0-4 2-4 5 0 1.5.5 3 1.5 4L7 16l1.5 1.5L11 15h2l2.5 2.5L17 16l-2.5-3c1-1 1.5-2.5 1.5-4 0-3-2-5-4-5zm-1 5a1 1 0 110-2 1 1 0 010 2zm2 0a1 1 0 110-2 1 1 0 010 2zM8 6L5 4m11 2l3-2" />
  ),
  egg: (
    <path d="M12 3C9 3 7 7 7 11c0 3.3 2.2 6 5 6s5-2.7 5-6c0-4-2-8-5-8zm0 9a2 2 0 110-4 2 2 0 010 4z" />
  ),
  fish: (
    <path d="M2 12s3-4 7-4c1.5 0 2.5.5 3.5 1.5L15 7l-1 5 1 5-2.5-2.5C11.5 15.5 10.5 16 9 16c-4 0-7-4-7-4zm8-1a1 1 0 110-2 1 1 0 010 2z" />
  ),
  peanuts: (
    <path d="M12 3c-2 0-3.5 1.5-3.5 3.5S10 10 12 10s3.5-1.5 3.5-3.5S14 3 12 3zm0 9c-2 0-3.5 1.5-3.5 3.5S10 19 12 19s3.5-1.5 3.5-3.5S14 12 12 12z" />
  ),
  soy: (
    <path d="M12 2C8 2 5 5.5 5 9c0 2 1 4 2.5 5.5L12 22l4.5-7.5C18 13 19 11 19 9c0-3.5-3-7-7-7zm0 10a3 3 0 110-6 3 3 0 010 6z" />
  ),
  milk: (
    <path d="M8 2v3L6 7v13a2 2 0 002 2h8a2 2 0 002-2V7l-2-2V2H8zm1 2h6v1.5H9V4zm-1 5h8v10H8V9z" />
  ),
  treenuts: (
    <path d="M12 2L8 8h8l-4-6zM7 9c-1 0-2 1-2 2.5S8 15 12 15s7-1.5 7-3.5S18 9 17 9H7zm5 8v4" />
  ),
  celery: (
    <path d="M12 22V10m-3 4c-2-2-3-5-2-8l3 1m6 3c2-2 3-5 2-8l-3 1M9 6c0-2 1-4 3-4s3 2 3 4" />
  ),
  mustard: (
    <path d="M10 2h4v4l2 2v2h-8V8l2-2V2zm8 10H6l-1 3h14l-1-3zM7 17h10v3a2 2 0 01-2 2H9a2 2 0 01-2-2v-3z" />
  ),
  sesame: (
    <>
      <circle cx="8" cy="8" r="2" />
      <circle cx="16" cy="8" r="2" />
      <circle cx="12" cy="14" r="2" />
      <circle cx="8" cy="18" r="1.5" />
      <circle cx="16" cy="18" r="1.5" />
    </>
  ),
  sulphites: (
    <path d="M12 2a8 8 0 00-4 15v3a2 2 0 002 2h4a2 2 0 002-2v-3A8 8 0 0012 2zm0 3a5 5 0 015 5c0 1.5-.7 2.9-1.8 3.8l-.2.2v5h-6v-5l-.2-.2A5 5 0 017 10a5 5 0 015-5z" />
  ),
  lupin: (
    <path d="M12 22v-8m0 0c-3-2-5-5-5-9 0-2 1-3 2-3 2 0 3 2 3 4m0 8c3-2 5-5 5-9 0-2-1-3-2-3-2 0-3 2-3 4" />
  ),
  molluscs: (
    <path d="M12 3C7 3 3 7 3 12c0 3 2 6 5 7.5V22h8v-2.5c3-1.5 5-4.5 5-7.5 0-5-4-9-9-9zm-2 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm4 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
  ),
};

const ALLERGEN_COLORS = {
  gluten: "#D97706",
  crustaceans: "#DC2626",
  egg: "#F59E0B",
  fish: "#2563EB",
  peanuts: "#92400E",
  soy: "#65A30D",
  milk: "#3B82F6",
  treenuts: "#A16207",
  celery: "#16A34A",
  mustard: "#CA8A04",
  sesame: "#78716C",
  sulphites: "#7C3AED",
  lupin: "#0D9488",
  molluscs: "#6366F1",
};

export function AllergenIcon({ icon, size = 24, color, className = "" }) {
  const fillColor = color || ALLERGEN_COLORS[icon] || "#666";
  const pathData = iconPaths[icon];
  if (!pathData) return null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fillColor}
      stroke="none"
      className={className}
      role="img"
      aria-hidden="true"
    >
      {pathData}
    </svg>
  );
}

export function AllergenBadge({ allergen, size = "sm", severity = "contains" }) {
  const sizeMap = { xs: 14, sm: 18, md: 24, lg: 32 };
  const iconSize = sizeMap[size] || 18;
  const bgOpacity = severity === "contains" ? "0.12" : severity === "may_contain" ? "0.08" : "0.05";
  const borderStyle = severity === "may_contain" ? "dashed" : "solid";
  const color = ALLERGEN_COLORS[allergen?.icon] || "#666";

  return (
    <div
      title={`${allergen?.name || ""}${severity === "may_contain" ? " (puede contener)" : severity === "trace" ? " (trazas)" : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: iconSize + 10,
        height: iconSize + 10,
        borderRadius: "50%",
        background: `rgba(${hexToRgb(color)}, ${bgOpacity})`,
        border: `1.5px ${borderStyle} ${color}40`,
      }}
      data-testid={`allergen-badge-${allergen?.slug || ""}`}
    >
      <AllergenIcon icon={allergen?.icon} size={iconSize} color={color} />
    </div>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

export { ALLERGEN_COLORS };
export default AllergenIcon;
